import axios from 'axios';

// Set to true to use mock data (for frontend development), set to false to use real API
const USE_MOCK_DATA = false;

// API base URL - use localhost:18080 for development, relative path for production
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:18080/api';

// Internal-gateway base URL - 运行在 18081 端口
const INTERNAL_GATEWAY_URL = process.env.NODE_ENV === 'production'
  ? '/v1'
  : 'http://localhost:18081/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 180 seconds, tests may need longer time
  headers: {
    'Content-Type': 'application/json',
  },
});

// Internal Gateway API 实例
const internalGatewayAPI = axios.create({
  baseURL: INTERNAL_GATEWAY_URL,
  timeout: 300000, // 5 分钟，同步操作可能需要较长时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// Internal Gateway 请求拦截器
internalGatewayAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

internalGatewayAPI.interceptors.response.use(
  (response) => {
    // 返回完整响应，包括 code 字段
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    // 对于 4xx/5xx 错误，尝试解析错误响应
    if (error.response?.data) {
      return Promise.reject(error.response.data);
    }
    return Promise.reject(error);
  }
);

// 导出 internalGatewayAPI
export { internalGatewayAPI };

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Ignore refresh-models errors (internal-gateway may not be running)
    if (error.config?.url?.includes('refresh-models')) {
      return Promise.reject(error);
    }
    if (error.response) {
      // Ignore models/types and models/usecases errors (optional config)
      if (error.config?.url?.includes('/models/types') || error.config?.url?.includes('/models/usecases')) {
        return Promise.reject(error);
      }
      switch (error.response.status) {
        case 401:
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
          break;
        case 403:
          console.error('No permission');
          break;
        default:
          console.error('Request failed:', error.response.data);
      }
    }
    return Promise.reject(error);
  }
);

// ============ Mock API ============
const mockDelay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

const mockAuthAPI = {
  login: async (email, password) => {
    await mockDelay();
    if (email === 'admin@example.com' || email === 'admin') {
      return {
        code: 0,
        message: 'Login successful',
        data: {
          token: 'mock_admin_token_' + Date.now(),
          user: {
            id: '1',
            email: email,
            username: 'admin',
            role: 'admin'
          }
        }
      };
    }
    return {
      code: 0,
      message: 'Login successful (mock)',
      data: {
        token: 'mock_user_token_' + Date.now(),
        user: {
          id: '2',
          email: email,
          username: 'test',
          role: 'user'
        }
      }
    };
  },
  logout: () => {
    localStorage.removeItem('admin_token');
    window.location.href = '/login';
  },
};

const mockStatsAPI = {
  getDashboard: async () => {
    await mockDelay();
    return {
      code: 0,
      data: {
        totalUsers: 128,
        totalRevenue: 15680.50,
        totalOrders: 342,
        totalCalls: 1256,
        callTrend: [
          { date: '2024-01-20', count: 856 },
          { date: '2024-01-21', count: 1024 },
          { date: '2024-01-22', count: 956 },
          { date: '2024-01-23', count: 1156 },
          { date: '2024-01-24', count: 1256 },
        ],
        revenueTrend: [
          { date: '2024-01-20', amount: 3200 },
          { date: '2024-01-21', amount: 4100 },
          { date: '2024-01-22', amount: 3800 },
          { date: '2024-01-23', amount: 5200 },
          { date: '2024-01-24', amount: 5680 },
        ]
      }
    };
  }
};

const mockUserAPI = {
  getList: async (page = 1, pageSize = 20, params = {}) => {
    await mockDelay();
    const users = [];
    for (let i = 1; i <= pageSize; i++) {
      const id = (page - 1) * pageSize + i;
      users.push({
        id: String(id),
        username: `user${id}`,
        email: `user${id}@example.com`,
        balance: (Math.random() * 1000).toFixed(2),
        status: Math.random() > 0.2 ? 'active' : 'inactive',
        created_at: `2024-01-${String(Math.floor(Math.random() * 30) + 1).padStart(2, '0')}`,
        api_key: `sk-${Math.random().toString(36).substring(2, 15)}`,
      });
    }
    return {
      code: 0,
      data: {
        list: users,
        total: 128,
        page: page,
        pageSize: pageSize
      }
    };
  },

  updateStatus: async (id, status) => {
    await mockDelay();
    return {
      code: 0,
      message: 'User status updated',
      data: { id, status }
    };
  },

  adjustBalance: async (id, amount, reason) => {
    await mockDelay();
    return {
      code: 0,
      message: 'Balance adjusted successfully',
      data: { id, amount: parseFloat(amount), reason }
    };
  },

  resetAPIKey: async (id) => {
    await mockDelay();
    return {
      code: 0,
      message: 'API Key reset successfully',
      data: { id, api_key: `sk-${Math.random().toString(36).substring(2, 15)}` }
    };
  }
};

const mockOrderAPI = {
  getList: async (page = 1, pageSize = 20, params = {}) => {
    await mockDelay();
    const orders = [];
    const statuses = ['pending', 'succeeded', 'failed', 'cancelled'];
    for (let i = 1; i <= pageSize; i++) {
      const id = (page - 1) * pageSize + i;
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      orders.push({
        id: (page - 1) * pageSize + i,
        user_id: Math.floor(Math.random() * 100) + 1,
        amount: (Math.random() * 500 + 10).toFixed(2),
        status: status,
        payment_method: 'stripe',
        created_at: `2024-01-${String(Math.floor(Math.random() * 30) + 1).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`
      });
    }
    return {
      code: 0,
      data: {
        list: orders,
        total: 342,
        page: page,
        pageSize: pageSize
      }
    };
  },

  getDetail: async (id) => {
    await mockDelay();
    return {
      code: 0,
      data: {
        id: id,
        user_id: '5',
        user_email: 'user5@example.com',
        amount: '199.00',
        currency: 'USD',
        status: 'paid',
        payment_method: 'stripe',
        payment_intent_id: 'pi_1234567890',
        created_at: '2024-01-24 14:30:00',
        updated_at: '2024-01-24 14:31:00'
      }
    };
  },

  cancelOrder: async (id) => {
    await mockDelay();
    return { code: 0, message: 'Order cancelled' };
  },

  deleteOrder: async (id) => {
    await mockDelay();
    return { code: 0, message: 'Order deleted' };
  }
};

// ============ Real API Definition ============
const realAuthAPI = {
  login: (username, password) => api.post('/admin/login', { username, password }),
  logout: () => {
    localStorage.removeItem('admin_token');
    window.location.href = '/login';
  },
};

const realUserAPI = {
  getList: (page = 1, pageSize = 20, params = {}) =>
    api.get('/admin/users', { params: { page, page_size: pageSize, ...params } }),
  getBalanceAdjustments: (id, page = 1, pageSize = 20) =>
    api.get(`/admin/users/${id}/balance-adjustments`, { params: { page, page_size: pageSize } }),
  getDetail: (id) => api.get(`/admin/users/${id}`),
  updateStatus: (id, status) => api.put(`/admin/users/${id}/status`, { status }),
  adjustBalance: (id, amount, reason) =>
    api.post(`/admin/users/${id}/balance`, { amount, reason }),
  resetAPIKey: (id) => api.post(`/admin/users/${id}/reset-key`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
};

const realOrderAPI = {
  getList: (page = 1, pageSize = 20, params = {}) =>
    api.get('/admin/orders', { params: { page, page_size: pageSize, ...params } }),
  getDetail: (id) => api.get(`/admin/orders/${id}`),
  cancelOrder: (id) => api.post(`/admin/orders/${id}/cancel`),
  deleteOrder: (id) => api.delete(`/admin/orders/${id}`),
  // 线下订单审核
  approveOfflineOrder: (orderNo, adminNotes) => api.post(`/admin/offline-orders/${orderNo}/approve`, { admin_notes: adminNotes }),
  rejectOfflineOrder: (orderNo, adminNotes) => api.post(`/admin/offline-orders/${orderNo}/reject`, { admin_notes: adminNotes }),
};

const realStatsAPI = {
  getDashboard: () => api.get('/admin/stats/dashboard'),
  getCallStats: (startDate, endDate) =>
    api.get('/admin/stats/calls', { params: { start_date: startDate, end_date: endDate } }),
  getRevenueStats: (startDate, endDate) =>
    api.get('/admin/stats/revenue', { params: { start_date: startDate, end_date: endDate } }),
};

// Provider Management API
const realProviderAPI = {
  getProviders: () => api.get('/admin/providers'),
  createProvider: (data) => api.post('/admin/providers', data),
  updateProvider: (id, data) => api.put(`/admin/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/admin/providers/${id}`),
  getHolidaySync: () => api.get('/admin/holiday-sync'),
  syncHolidays: () => api.post('/admin/holiday-sync'),
  getModelConfigs: () => api.get('/admin/model-configs'),
  createModelConfig: (data) => api.post('/admin/model-configs', data),
  updateModelConfig: (id, data) => api.put(`/admin/model-configs/${id}`, data),
  deleteModelConfig: (id) => api.delete(`/admin/model-configs/${id}`),
  setModelHot: (id, isHot) => api.put(`/admin/model-configs/${id}`, { is_hot: isHot }),
  setModelStatus: (id, status) => api.put(`/admin/model-configs/${id}`, { status }),
  updateModelFallback: (id, fallbackModels) => api.put(`/admin/model-configs/${id}`, { fallback_models: JSON.stringify(fallbackModels) }),
  refreshGatewayModels: () => api.post('/admin/refresh-models'),
  syncOpenRouterModels: () => internalGatewayAPI.post('/sync/openrouter'),
  // 批量添加 OpenRouter 模型
  batchAddModels: (data) => api.post('/admin/models/batch-add', data),
  // 删除模型
  deleteModel: (modelName) => api.delete(`/admin/models/${encodeURIComponent(modelName)}`),
  // Test provider connection
  testProviderConnection: (data) => api.post('/admin/providers/test', data),
  getSeedanceBalance: (params) => api.get('/admin/seedance/balance', { params }),
  getSeedanceBills: (params) => api.get('/admin/seedance/bills', { params }),
  getSeedanceUsage: (params) => api.get('/admin/seedance/usage', { params }),
  // Test model config
  testModelConfig: (data) => api.post('/admin/model-configs/test', data),
  // 健康检查相关
  getHealthStatuses: () => internalGatewayAPI.get('/health/providers'),
  markProviderUnhealthy: (id, duration) => internalGatewayAPI.post(`/admin/provider/${id}/unhealthy`, { duration }),
  markProviderHealthy: (id) => internalGatewayAPI.post(`/admin/provider/${id}/healthy`),
  // 限流配置相关
  getRateLimitConfig: () => api.get('/admin/ratelimit/config'),
  updateRateLimitConfig: (data) => api.put('/admin/ratelimit/config', data),
  // 路由策略相关
  getRouterMetrics: () => internalGatewayAPI.get('/admin/router/metrics'),
  getRouterStrategy: () => internalGatewayAPI.get('/admin/router/default-strategy'),
  setRouterStrategy: (strategy) => internalGatewayAPI.put('/admin/router/default-strategy', { strategy }),

  // Real Name Auth management
  getRealNameAuths: (page, pageSize, params) => realRealNameAuthAPI.getList(page, pageSize, params),
  getRealNameAuthDetail: (id) => realRealNameAuthAPI.getDetail(id),
  retryRealNameFace: (id) => realRealNameAuthAPI.retryFace(id),
  resolveRealNameFace: (id, decision, evidence) => realRealNameAuthAPI.resolveFace(id, decision, evidence),
  approveRealNameAuth: (id) => realRealNameAuthAPI.approve(id),
  rejectRealNameAuth: (id, reason) => realRealNameAuthAPI.reject(id, reason),
};

// Affiliate Management API
const realAffiliateAPI = {
  getAffiliates: (params = {}) =>
    api.get('/admin/affiliates', { params }),
  setAffiliate: (userId, data) => api.post(`/admin/users/${userId}/affiliate`, data),
  setMerchant: (userId, data) => api.post(`/admin/users/${userId}/merchant`, data),
  updateAffiliateStatus: (affiliateId, status) =>
    api.put(`/admin/affiliates/${affiliateId}/status`, { status }),
  updateAffiliate: (affiliateId, data) =>
    api.put(`/admin/affiliates/${affiliateId}`, data),
  getWithdrawals: (status = '') =>
    api.get('/admin/withdrawals', { params: { status } }),
  processWithdrawal: (id, data) => api.post(`/admin/withdrawals/${id}/process`, data),
};

// System Configuration API
const realConfigAPI = {
  getConfigs: () => api.get('/admin/configs'),
  getConfigByKey: (key) => api.get(`/admin/configs/key/${key}`),
  updateConfig: (id, value) => api.put(`/admin/configs/${id}`, { config_value: value }),
  updateConfigByKey: (key, value) => api.put(`/admin/configs/key/${key}`, { config_value: value }),
};

// Billing Management API
const realBillAPI = {
  getBills: (page = 1, pageSize = 20, params = {}) =>
    api.get('/admin/bills', { params: { page, page_size: pageSize, ...params } }),
  getUsageStats: (startDate, endDate) =>
    api.get('/admin/usage-stats', { params: { start_date: startDate, end_date: endDate } }),
};

// Referral Campaign API
const realReferralCampaignAPI = {
  getCampaigns: () => api.get('/admin/referral-campaigns'),
  getCampaign: (id) => api.get(`/admin/referral-campaigns/${id}`),
  createCampaign: (data) => api.post('/admin/referral-campaigns', data),
  updateCampaign: (id, data) => api.put(`/admin/referral-campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/admin/referral-campaigns/${id}`),
};

// Feedback Management API
const realFeedbackAPI = {
  getFeedbacks: (params = {}) =>
    api.get('/admin/feedbacks', { params }),
  getFeedback: (id) => api.get(`/admin/feedbacks/${id}`),
  replyFeedback: (id, reply) => api.post(`/admin/feedbacks/${id}/reply`, { reply }),
  closeFeedback: (id) => api.post(`/admin/feedbacks/${id}/close`),
  getFeedbackMessages: (id) => api.get(`/admin/feedbacks/${id}/messages`),
};

// File Upload API
const realUploadAPI = {
  upload: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // 上传支付证书（微信/支付宝）
  uploadPaymentCert: (certType, file) => {
    const formData = new FormData();
    formData.append('cert_type', certType);
    formData.append('file', file);
    return api.post('/admin/payment-configs/upload-cert', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// API Key Management API
const realAPIKeyAPI = {
  getList: (page = 1, pageSize = 20, params = {}) =>
    api.get('/admin/api-keys', { params: { page, page_size: pageSize, ...params } }),
  toggle: (id) => api.post(`/admin/api-keys/${id}/toggle`),
};

// System Maintenance API
const realSystemAPI = {
  getSystemStatus: () => api.get('/admin/system/status'),
  setSystemSwitch: (enabled) => api.post('/admin/system/switch', { enabled }),
  syncAllBalances: () => api.post('/admin/system/sync'),
  getBalanceComparison: () => api.get('/admin/system/balance-comparison'),
  // Merchant commission verification
  verifyMerchantCommission: (affiliateId) => api.get('/admin/system/verify-merchant-commission', { 
    params: affiliateId ? { affiliate_id: affiliateId } : {} 
  }),
  syncMerchantCommission: (affiliateId) => api.post('/admin/system/sync-merchant-commission', 
    affiliateId ? { affiliate_id: affiliateId } : {}
  ),
  // End-to-end testing
  registerTestUser: () => api.post('/admin/system/test-register'),
  testDeepSeek: (apiKey) => api.post('/admin/system/test-deepseek', { api_key: apiKey }),
  getTestUserBalance: (userId) => api.get(`/admin/system/test-balance/${userId}`),
  runFullTest: (model) => api.get('/admin/system/full-test', { params: { model } }),
  runMerchantCommissionTest: (model) => api.get('/admin/system/merchant-commission-test', { params: { model } }),
  getMerchantCommissionTestProgress: (testId) => api.get('/admin/system/merchant-commission-progress', { params: { test_id: testId } }),
  getTestProgress: (testId) => api.get('/admin/system/test-status', { params: { test_id: testId } }),
  clearCache: () => api.post('/admin/system/clear-cache'),
  // Redis Stream management
  getStreamStatus: () => api.get('/admin/system/stream-status'),
  retryPendingMessages: (group) => api.post('/admin/system/stream-retry', { group }),
  retryAllPendingMessages: () => api.post('/admin/system/stream-retry-v2'),
  cleanPendingMessages: (group) => api.post('/admin/system/stream-clean', { group }),
  trimProcessedMessages: (params) => api.post('/admin/system/stream-trim', params),
  // Consumer config
  getConsumerConfig: () => api.get('/admin/system/consumer-config'),
  updateConsumerConfig: (params) => api.post('/admin/system/consumer-config', params),
};

// Real Name Auth Management API
const realRealNameAuthAPI = {
  getList: (page = 1, pageSize = 20, params = {}) =>
    api.get('/admin/realname-auths', { params: { page, page_size: pageSize, ...params } }),
  getDetail: (id) => api.get(`/admin/realname-auths/${id}`),
  retryFace: (id) => api.post(`/admin/realname-auths/${id}/face-retry`),
  resolveFace: (id, decision, evidence) => api.post(`/admin/realname-auths/${id}/face-resolve`, { decision, evidence }),
  approve: (id) => api.post(`/admin/realname-auths/${id}/approve`),
  reject: (id, reason) => api.post(`/admin/realname-auths/${id}/reject`, { reason }),
};

// Plugin Management API
const realPluginAPI = {
  // Built-in plugins
  getBuiltInPlugins: () => api.get('/admin/plugins'),
  getBuiltInPlugin: (name) => api.get(`/admin/plugins/${name}`),
  // Custom plugins
  getCustomPlugins: (params = {}) => api.get('/admin/plugins/custom', { params }),
  getCustomPlugin: (id) => api.get(`/admin/plugins/custom/${id}`),
  createCustomPlugin: (data) => api.post('/admin/plugins/custom', data),
  updateCustomPlugin: (id, data) => api.put(`/admin/plugins/custom/${id}`, data),
  deleteCustomPlugin: (id) => api.delete(`/admin/plugins/custom/${id}`),
  executeCustomPlugin: (id, params) => api.post(`/admin/plugins/custom/${id}/execute`, params),
};

// Preset Management API
const realPresetAPI = {
  // Admin APIs
  getAdminPresets: (params = {}) => api.get('/admin/presets', { params }),
  getAdminPreset: (id) => api.get(`/admin/presets/${id}`),
  deleteAdminPreset: (id) => api.delete(`/admin/presets/${id}`),
  // Approval APIs
  approvePreset: (id) => api.post(`/admin/presets/${id}/approve`),
  rejectPreset: (id, note) => api.post(`/admin/presets/${id}/reject`, { note }),
  getPendingPresets: (params = {}) => api.get('/admin/presets/pending', { params }),
  // User APIs
  getPresets: (params = {}) => api.get('/v1/presets', { params }),
  getPreset: (id) => api.get(`/v1/presets/${id}`),
  createPreset: (data) => api.post('/v1/presets', data),
  updatePreset: (id, data) => api.put(`/v1/presets/${id}`, data),
  deletePreset: (id) => api.delete(`/v1/presets/${id}`),
  clonePreset: (id, name) => api.post(`/admin/presets/${id}/clone`, { name }),
};

// ============ Select API based on config ============
if (USE_MOCK_DATA && process.env.NODE_ENV === 'development') {
  console.log('Using mock data mode');
}

export const authAPI = USE_MOCK_DATA ? mockAuthAPI : realAuthAPI;
export const userAPI = USE_MOCK_DATA ? mockUserAPI : realUserAPI;
export const orderAPI = USE_MOCK_DATA ? mockOrderAPI : realOrderAPI;
export const statsAPI = USE_MOCK_DATA ? mockStatsAPI : realStatsAPI;
export const providerAPI = USE_MOCK_DATA ? {} : realProviderAPI;
export const affiliateAPI = USE_MOCK_DATA ? {} : realAffiliateAPI;
export const configAPI = USE_MOCK_DATA ? {} : realConfigAPI;
export const billAPI = USE_MOCK_DATA ? {} : realBillAPI;
export const referralCampaignAPI = USE_MOCK_DATA ? {} : realReferralCampaignAPI;
export const feedbackAPI = USE_MOCK_DATA ? {} : realFeedbackAPI;
export const uploadAPI = USE_MOCK_DATA ? {} : realUploadAPI;
export const apiKeyAPI = USE_MOCK_DATA ? {} : realAPIKeyAPI;
export const systemAPI = USE_MOCK_DATA ? {} : realSystemAPI;
export const pluginAPI = USE_MOCK_DATA ? {} : realPluginAPI;
export const presetAPI = USE_MOCK_DATA ? {} : realPresetAPI;
export const realNameAuthAPI = USE_MOCK_DATA ? {} : realRealNameAuthAPI;

// Unified export adminAPI
export const adminAPI = {
  // User management
  getUsers: (page, pageSize, params) => realUserAPI.getList(page, pageSize, params),
  updateUserStatus: (id, status) => realUserAPI.updateStatus(id, status),
  adjustUserBalance: (id, amount, reason) => realUserAPI.adjustBalance(id, amount, reason),
  resetUserAPIKey: (id) => realUserAPI.resetAPIKey(id),

  // Order management
  getOrders: (page, pageSize, params) => realOrderAPI.getList(page, pageSize, params),
  cancelOrder: (id) => realOrderAPI.cancelOrder(id),
  deleteOrder: (id) => realOrderAPI.deleteOrder(id),
  // 线下订单审核
  approveOfflineOrder: (orderNo, adminNotes) => realOrderAPI.approveOfflineOrder(orderNo, adminNotes),
  rejectOfflineOrder: (orderNo, adminNotes) => realOrderAPI.rejectOfflineOrder(orderNo, adminNotes),

  // Statistics
  getDashboardStats: () => realStatsAPI.getDashboard(),

  // Provider management
  getProviders: () => realProviderAPI.getProviders(),
  createProvider: (data) => realProviderAPI.createProvider(data),
  updateProvider: (id, data) => realProviderAPI.updateProvider(id, data),
  deleteProvider: (id) => realProviderAPI.deleteProvider(id),
  getHolidaySync: () => realProviderAPI.getHolidaySync(),
  syncHolidays: () => realProviderAPI.syncHolidays(),
  getModelConfigs: () => realProviderAPI.getModelConfigs(),
  createModelConfig: (data) => realProviderAPI.createModelConfig(data),
  updateModelConfig: (id, data) => realProviderAPI.updateModelConfig(id, data),
  deleteModelConfig: (id) => realProviderAPI.deleteModelConfig(id),
  setModelHot: (id, isHot) => realProviderAPI.setModelHot(id, isHot),
  setModelStatus: (id, status) => realProviderAPI.setModelStatus(id, status),
  updateModelFallback: (id, fallbackModels) => realProviderAPI.updateModelConfig(id, { fallback_models: JSON.stringify(fallbackModels) }),
  refreshGatewayModels: () => realProviderAPI.refreshGatewayModels(),
  // Test provider connection
  testProviderConnection: (data) => realProviderAPI.testProviderConnection(data),
  // Test model config
  testModelConfig: (data) => realProviderAPI.testModelConfig(data),

  // Affiliate management
  getAffiliates: (page, pageSize) => realAffiliateAPI.getAffiliates(page, pageSize),
  setAffiliate: (userId, data) => realAffiliateAPI.setAffiliate(userId, data),
  setMerchant: (userId, data) => realAffiliateAPI.setMerchant(userId, data),
  updateAffiliateStatus: (affiliateId, status) => realAffiliateAPI.updateAffiliateStatus(affiliateId, status),
  updateAffiliate: (affiliateId, data) => realAffiliateAPI.updateAffiliate(affiliateId, data),
  getWithdrawals: (status) => realAffiliateAPI.getWithdrawals(status),
  processWithdrawal: (id, data) => realAffiliateAPI.processWithdrawal(id, data),

  // System configuration
  getConfigs: () => realConfigAPI.getConfigs(),
  getConfigByKey: (key) => realConfigAPI.getConfigByKey(key),
  updateConfig: (id, value) => realConfigAPI.updateConfig(id, value),
  updateConfigByKey: (key, value) => realConfigAPI.updateConfigByKey(key, value),

  // Billing
  getBills: (page, pageSize, params) => realBillAPI.getBills(page, pageSize, params),
  getUsageStats: (startDate, endDate) => realBillAPI.getUsageStats(startDate, endDate),

  // Referral campaign
  getReferralCampaigns: () => realReferralCampaignAPI.getCampaigns(),
  getReferralCampaign: (id) => realReferralCampaignAPI.getCampaign(id),
  createReferralCampaign: (data) => realReferralCampaignAPI.createCampaign(data),
  updateReferralCampaign: (id, data) => realReferralCampaignAPI.updateCampaign(id, data),
  deleteReferralCampaign: (id) => realReferralCampaignAPI.deleteCampaign(id),

  // Agent (OEM) management
  getAgents: () => api.get('/admin/agents'),
  createAgent: (data) => api.post('/admin/agents', data),
  updateAgent: (id, data) => api.put(`/admin/agents/${id}`, data),
  toggleAgent: (id) => api.post(`/admin/agents/${id}/toggle`),
  impersonateAgent: (id) => api.post(`/admin/agents/${id}/impersonate`),
  deleteAgent: (id) => api.delete(`/admin/agents/${id}`),

  // Feedback
  getFeedbacks: (params) => realFeedbackAPI.getFeedbacks(params),
  getFeedback: (id) => realFeedbackAPI.getFeedback(id),
  replyFeedback: (id, reply) => realFeedbackAPI.replyFeedback(id, reply),
  closeFeedback: (id) => realFeedbackAPI.closeFeedback(id),
  getFeedbackMessages: (id) => realFeedbackAPI.getFeedbackMessages(id),

  // System maintenance
  getSystemStatus: () => realSystemAPI.getSystemStatus(),
  setSystemSwitch: (enabled) => realSystemAPI.setSystemSwitch(enabled),
  syncAllBalances: () => realSystemAPI.syncAllBalances(),
  getBalanceComparison: () => realSystemAPI.getBalanceComparison(),
  verifyMerchantCommission: (affiliateId) => realSystemAPI.verifyMerchantCommission(affiliateId),
  syncMerchantCommission: (affiliateId) => realSystemAPI.syncMerchantCommission(affiliateId),

  // End-to-end testing
  registerTestUser: () => realSystemAPI.registerTestUser(),
  testDeepSeek: (apiKey) => realSystemAPI.testDeepSeek(apiKey),
  getTestUserBalance: (userId) => realSystemAPI.getTestUserBalance(userId),
  runFullTest: (model) => realSystemAPI.runFullTest(model),
  runMerchantCommissionTest: (model) => realSystemAPI.runMerchantCommissionTest(model),
  getMerchantCommissionTestProgress: (testId) => realSystemAPI.getMerchantCommissionTestProgress(testId),
  getTestProgress: (testId) => realSystemAPI.getTestProgress(testId),
  clearCache: () => realSystemAPI.clearCache(),
  // Redis Stream management
  getStreamStatus: () => realSystemAPI.getStreamStatus(),
  retryPendingMessages: (group) => realSystemAPI.retryPendingMessages(group),
  retryAllPendingMessages: () => realSystemAPI.retryAllPendingMessages(),
  cleanPendingMessages: (group) => realSystemAPI.cleanPendingMessages(group),
  trimProcessedMessages: (params) => realSystemAPI.trimProcessedMessages(params),
  // Consumer config
  getConsumerConfig: () => realSystemAPI.getConsumerConfig(),
  updateConsumerConfig: (params) => realSystemAPI.updateConsumerConfig(params),

  // Plugin management
  getBuiltInPlugins: () => realPluginAPI.getBuiltInPlugins(),
  getBuiltInPlugin: (name) => realPluginAPI.getBuiltInPlugin(name),
  getCustomPlugins: (params) => realPluginAPI.getCustomPlugins(params),
  getCustomPlugin: (id) => realPluginAPI.getCustomPlugin(id),
  createCustomPlugin: (data) => realPluginAPI.createCustomPlugin(data),
  updateCustomPlugin: (id, data) => realPluginAPI.updateCustomPlugin(id, data),
  deleteCustomPlugin: (id) => realPluginAPI.deleteCustomPlugin(id),

  // Preset management (admin)
  getAdminPresets: (params) => realPresetAPI.getAdminPresets(params),
  getAdminPreset: (id) => realPresetAPI.getAdminPreset(id),
  deleteAdminPreset: (id) => realPresetAPI.deleteAdminPreset(id),
  clonePreset: (id, name) => realPresetAPI.clonePreset(id, name),
  approvePreset: (id) => realPresetAPI.approvePreset(id),
  rejectPreset: (id, note) => realPresetAPI.rejectPreset(id, note),
  getPendingPresets: (params) => realPresetAPI.getPendingPresets(params),

  // Batch management (admin)
  getAdminBatches: (params) => api.get('/admin/batches', { params }),
  getAdminBatch: (id) => api.get(`/admin/batches/${id}`),
  deleteAdminBatch: (id) => api.delete(`/admin/batches/${id}`),

  // Rate limit management
  getRateLimitConfig: () => internalGatewayAPI.get('/admin/ratelimit'),
  updateRateLimitConfig: (data) => internalGatewayAPI.put('/admin/ratelimit', data),
  resetRateLimit: (url) => internalGatewayAPI.post(url),
  getUsageOverview: () => internalGatewayAPI.get('/admin/usage/overview'),
  getTopUsers: (limit) => internalGatewayAPI.get('/admin/usage/top-users', { params: { limit } }),
  getBlockedUsers: () => internalGatewayAPI.get('/admin/usage/blocked'),
  getUsageStats: () => internalGatewayAPI.get('/admin/usage'),
  applyUserLimit: (data) => internalGatewayAPI.post('/admin/usage/user-limit', data),

  // Router management
  getRouteStrategies: () => internalGatewayAPI.get('/admin/router/strategies'),
  getRouteMetrics: () => internalGatewayAPI.get('/admin/router/metrics'),
  getAvailableModels: (modelType) => internalGatewayAPI.get('/admin/router/models', { params: { model_type: modelType } }),
  refreshModels: () => internalGatewayAPI.post('/admin/router/models/refresh'),
  getFallbackModels: (modelType) => internalGatewayAPI.get('/admin/router/fallback', { params: { model_type: modelType } }),
  updateFallbackModels: (data) => internalGatewayAPI.put('/admin/router/fallback', data),
  testRoute: (data) => internalGatewayAPI.post('/admin/router/test', data),
  updateRetryConfig: (data) => internalGatewayAPI.put('/admin/router/retry', data),
  getDefaultStrategy: () => internalGatewayAPI.get('/admin/router/default-strategy'),
  setDefaultStrategy: (strategy) => internalGatewayAPI.put('/admin/router/default-strategy', { strategy }),

  // User fallback management
  getUserFallback: (userId) => api.get(`/admin/users/${userId}/fallback`),
  updateUserFallback: (userId, fallbackModels) => api.put(`/admin/users/${userId}/fallback`, { fallback_models: fallbackModels }),

  // Health check management
  getHealthStatuses: () => internalGatewayAPI.get('/health/providers'),
  markProviderUnhealthy: (id, duration) => internalGatewayAPI.post(`/admin/provider/${id}/unhealthy`, { duration }),
  markProviderHealthy: (id) => internalGatewayAPI.post(`/admin/provider/${id}/healthy`),
  getRouterMetrics: () => internalGatewayAPI.get('/admin/router/metrics'),
  getRouterStrategy: () => internalGatewayAPI.get('/admin/router/default-strategy'),
  setRouterStrategy: (strategy) => internalGatewayAPI.put('/admin/router/default-strategy', { strategy }),

  // Real Name Auth management
  getRealNameAuths: (page, pageSize, params) => realRealNameAuthAPI.getList(page, pageSize, params),
  getRealNameAuthDetail: (id) => realRealNameAuthAPI.getDetail(id),
  retryRealNameFace: (id) => realRealNameAuthAPI.retryFace(id),
  resolveRealNameFace: (id, decision, evidence) => realRealNameAuthAPI.resolveFace(id, decision, evidence),
  approveRealNameAuth: (id) => realRealNameAuthAPI.approve(id),
  rejectRealNameAuth: (id, reason) => realRealNameAuthAPI.reject(id, reason),

  // Upload
  uploadPaymentCert: (certType, file) => realUploadAPI.uploadPaymentCert(certType, file),

  // 公司动态
  getCompanyNews: (params) => api.get('/admin/news', { params }),
  createCompanyNews: (data) => api.post('/admin/news', data),
  updateCompanyNews: (id, data) => api.put(`/admin/news/${id}`, data),
  deleteCompanyNews: (id) => api.delete(`/admin/news/${id}`),
};

export default api;
